import React, { useEffect, useState, ReactNode } from "react";
import { initializeAndTrack } from "gatsby-plugin-gdpr-cookies";
import {
  COOKIE,
  getCookieValue,
  pushGoogleConsentUpdate,
  setCookie,
  shouldLoadGoogleTagManager,
} from "../utils/consentCookies";

const COOKIE_DAYS = 365;

type CookieRow = {
  id: string;
  editable: boolean;
  default: boolean;
  title: string;
  text: string;
  mirrored?: readonly string[];
};

const COOKIE_ROWS: CookieRow[] = [
  {
    id: "necessary",
    editable: false,
    default: true,
    title: "Essentiel",
    text: "Les cookies essentiels sont nécessaires au bon fonctionnement du site.",
  },
  {
    id: COOKIE.ANALYTICS,
    editable: true,
    default: true,
    title: "Mesure d'audience (Google Analytics)",
    text: "Permet de mesurer l'audience du site via Google Analytics",
    mirrored: [COOKIE.FUNCTIONALITY, COOKIE.PERSONALIZATION],
  },
  {
    id: COOKIE.AD_STORAGE,
    editable: true,
    default: true,
    title: "Google Ads",
    text: "Permet la mesure publicitaire et les conversions Google Ads",
    mirrored: [COOKIE.AD_USER_DATA, COOKIE.AD_PERSONALIZATION],
  },
  {
    id: COOKIE.CLARITY,
    editable: true,
    default: true,
    title: "Microsoft Clarity",
    text: "Permet l'analyse des parcours utilisateurs via Microsoft Clarity",
  },
  {
    id: COOKIE.GETQUANTY,
    editable: true,
    default: true,
    title: "GetQuanty",
    text: "GetQuanty permet d'identifier les entreprises qui visitent le site.",
  },
];

function cookieExists(name: string): boolean {
  return getCookieValue(name) !== null;
}

function writeRowCookies(row: CookieRow, value: boolean, expiresDays: number): void {
  const v = value ? "true" : "false";
  setCookie(row.id, v, expiresDays);
  if (row.mirrored) {
    row.mirrored.forEach((n) => setCookie(n, v, expiresDays));
  }
}

function syncGtmLoadCookie(expiresDays: number): void {
  const analytics = getCookieValue(COOKIE.ANALYTICS) === "true";
  const ads = getCookieValue(COOKIE.AD_STORAGE) === "true";
  const clarity = getCookieValue(COOKIE.CLARITY) === "true";
  const loadGtm = shouldLoadGoogleTagManager(analytics, ads, clarity);
  setCookie(COOKIE.GTM_LOAD, loadGtm ? "true" : "false", expiresDays);
}

function gatsbyLocationFromWindow(): {
  pathname: string;
  search: string;
  hash: string;
} {
  if (typeof window === "undefined") {
    return { pathname: "/", search: "", hash: "" };
  }
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  };
}

function applyConsentAndTrack(): void {
  pushGoogleConsentUpdate();
  initializeAndTrack(gatsbyLocationFromWindow());
}

type Props = {
  children: ReactNode;
  backgroundWrapperClasses?: string;
  backgroundClasses?: string;
  wrapperClasses?: string;
  acceptButtonText?: string;
  acceptButtonClasses?: string;
  buttonWrapperClasses?: string;
  declineButton?: boolean;
  declineButtonText?: string;
  declineButtonClasses?: string;
  personalizeButtonEnable?: boolean;
  personalizeButtonText?: string;
  personalizeButtonClasses?: string;
  personalizeValidationText?: string;
  personalizeValidationClasses?: string;
  personalizeValidationWrapperClasses?: string;
  cookieListClasses?: string;
  cookieMapClasses?: string;
  cookieTitleClasses?: string;
};

export function CookieConsentBanner({
  children,
  backgroundWrapperClasses = "",
  backgroundClasses = "",
  wrapperClasses = "",
  acceptButtonText = "Accepter",
  acceptButtonClasses = "",
  buttonWrapperClasses = "",
  declineButton = true,
  declineButtonText = "Refuser",
  declineButtonClasses = "",
  personalizeButtonEnable = true,
  personalizeButtonText = "Personnaliser",
  personalizeButtonClasses = "",
  personalizeValidationText = "Enregistrer mes préférences",
  personalizeValidationClasses = "",
  personalizeValidationWrapperClasses = "",
  cookieListClasses = "",
  cookieMapClasses = "",
  cookieTitleClasses = "",
}: Props) {
  const [displayCookieList, setDisplayCookieList] = useState(false);
  const [displayCookieNotice, setDisplayCookieNotice] = useState(false);

  useEffect(() => {
    const anyMissing = COOKIE_ROWS.some((row) => !cookieExists(row.id));
    if (anyMissing) {
      setDisplayCookieNotice(true);
    }
  }, []);

  const persistAllRows = (getValue: (row: CookieRow) => boolean) => {
    COOKIE_ROWS.forEach((row) => {
      if (!row.editable) {
        setCookie(row.id, row.default ? "true" : "false", COOKIE_DAYS);
        return;
      }
      writeRowCookies(row, getValue(row), COOKIE_DAYS);
    });
    syncGtmLoadCookie(COOKIE_DAYS);
  };

  const acceptCookie = () => {
    persistAllRows(() => true);
    setDisplayCookieNotice(false);
    applyConsentAndTrack();
  };

  const declineCookie = () => {
    COOKIE_ROWS.forEach((row) => {
      if (!row.editable) {
        setCookie(row.id, row.default ? "true" : "false", COOKIE_DAYS);
      } else {
        writeRowCookies(row, false, COOKIE_DAYS);
      }
    });
    syncGtmLoadCookie(COOKIE_DAYS);
    setDisplayCookieNotice(false);
    applyConsentAndTrack();
  };

  const validatePreferences = (e: React.FormEvent) => {
    e.preventDefault();
    COOKIE_ROWS.forEach((row, index) => {
      if (!row.editable) {
        setCookie(row.id, row.default ? "true" : "false", COOKIE_DAYS);
        return;
      }
      const el = document.getElementById(`cc-row-${index}`) as HTMLInputElement | null;
      const checked = el ? el.checked : row.default;
      writeRowCookies(row, checked, COOKIE_DAYS);
    });
    syncGtmLoadCookie(COOKIE_DAYS);
    setDisplayCookieNotice(false);
    applyConsentAndTrack();
  };

  return (
    <>
      {displayCookieNotice && (
        <div className={backgroundWrapperClasses}>
          <div className={backgroundClasses}>
            {children}
            {displayCookieList && (
              <form onSubmit={validatePreferences}>
                <div className={cookieListClasses}>
                  {COOKIE_ROWS.map((row, index) => (
                    <div key={row.id + "-" + index} className={cookieMapClasses}>
                      <input
                        type="checkbox"
                        defaultChecked={row.default}
                        disabled={!row.editable}
                        id={`cc-row-${index}`}
                      />
                      <label htmlFor={`cc-row-${index}`} className={cookieTitleClasses}>
                        {row.title}
                      </label>
                      <p>{row.text}</p>
                    </div>
                  ))}
                </div>
                <div className={personalizeValidationWrapperClasses}>
                  <button type="submit" className={personalizeValidationClasses}>
                    {personalizeValidationText}
                  </button>
                </div>
              </form>
            )}
            {!displayCookieList && (
              <div className={wrapperClasses}>
                {personalizeButtonEnable && (
                  <button
                    type="button"
                    onClick={() => setDisplayCookieList(true)}
                    className={personalizeButtonClasses}
                  >
                    {personalizeButtonText}
                  </button>
                )}
                <div className={buttonWrapperClasses}>
                  {declineButton && (
                    <button
                      type="button"
                      onClick={declineCookie}
                      className={declineButtonClasses}
                    >
                      {declineButtonText}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={acceptCookie}
                    className={acceptButtonClasses}
                  >
                    {acceptButtonText}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
